import { Div, Ref, RefString, Checkbox, MultilineInput, NumberInput, TextInput, Button, Line, Select, SelectOption } from "@tblabs/truffle";
import { Table } from "@tblabs/truffle/Controls/TableBuilder/Table";
import { Device } from "../../Services/Device";
import { LedStatus } from "../../Services/LedStatus";
import { PrimaryButton } from "../../Components/PrimaryButton";
import { IoStateViewer } from "./IoStateViewer";
import { Tabs } from "../../Components/Tabs/Tabs";
import { SettingsEdit } from "./SettingsEdit.1";


export class SettingsEditor extends Div
{
    private ioStateViewer = new IoStateViewer();

    constructor(private settings: SettingsEdit, private device: Device)
    {
        super();

        const tab = new RefString("General").Storable("settings-tab");

        const status = new Ref<LedStatus>(LedStatus.Start);
        const greenLed = new Ref<number>(1);
        const redLed = new Ref<number>(1);

        this.Append(
            new Tabs(tab)
                .Add("General", () =>
                    new Table([], [
                        ["Halt", new Checkbox(this.settings.halt)],
                        ["Servers pool", new MultilineInput(this.settings.serversPool).Rows(6)],
                        ["Max idle time (ms)", new NumberInput(this.settings.maxIdleTime)],
                        ["Min picture size (bytes)", new NumberInput(this.settings.minPictureSize)],
                        ["Delay between pictures (ms)", new NumberInput(this.settings.delayBetweenPictures)],
                    ]).Class("uk-table uk-table-small uk-table-striped"))
                .Add("Recorder", () => new Div().Append(
                    this.settings.HasRecorder
                        ? new Table([], [
                            ["Record pictures", new Checkbox(this.settings.recordPictures)],
                            ["Recorder storage dir", new TextInput(this.settings.recorderStorageDir)],
                            ["Recorder free space limit (bytes)", new NumberInput(this.settings.recorderFreeSpaceLimit)],
                        ]).Class("uk-table uk-table-small uk-table-striped")
                        : "- not available -"))
                .Add("Picture", () =>
                    new Table([], [
                        ["Preset name", new TextInput(this.settings.picturePresetName)],
                        ["Width", new NumberInput(this.settings.pictureWidth)],
                        ["Height", new NumberInput(this.settings.pictureHeight)],
                        ["Compression", new NumberInput(this.settings.pictureCompression)],
                        ["V flip", new NumberInput(this.settings.pictureVFlip)],
                        ["H flip", new NumberInput(this.settings.pictureHFlip)],
                        ["Brightness", new NumberInput(this.settings.pictureBrightness)],
                        ["Contrast", new NumberInput(this.settings.pictureContrast)],
                        ["Sharpness", new NumberInput(this.settings.pictureSharpness)],
                        ["Rotation", new NumberInput(this.settings.pictureRotation)],
                    ]).Class("uk-table uk-table-small uk-table-striped"))
                .Add("IO", () =>
                    new Div().Append(
                        new Table([], [
                            ["Vibration enabled", new Checkbox(this.settings.vibrationEnabled)],
                            ["Touch to vibration loopback", new Checkbox(this.settings.touchToVibrationLoopbackEnabled)],
                            ["Startup vibration", new Checkbox(this.settings.startupVibrationEnabled)],
                            ["Connection vibration", new Checkbox(this.settings.connectionVibrationEnabled)],
                            ["Touch to vibration loopback duration (ms)", new NumberInput(this.settings.touchToVibrationLoopbackDuration)],
                            ["Startup vibration duration (ms)", new NumberInput(this.settings.startupVibrationDuration)],
                            ["Inputs throttle time (ms) [restart required]", new NumberInput(this.settings.inputThrottleTime)],
                            ["Malfunction detector samples [restart required]", new NumberInput(this.settings.malfunctionDetectorSamples)],
                            ["Malfunction detector threshold [restart required]", new NumberInput(this.settings.malfunctionDetectorThreshold)],
                            [new Div().Append("IO state", "\n\n",
                                new PrimaryButton("REFRESH", async (btn) =>{
                                    btn.Label("Refreshing...");
                                    await this.RefreshIoState();
                                    btn.Label("REFRESH");
                                })),
                            this.ioStateViewer],
                        ]).Class("uk-table uk-table-small uk-table-striped"),

                    ))
                .Add("Led", () =>
                    new Div().MarginTop(16).Append(
                        new Button("Led On")
                            .OnClick(() => device.LedOn()),
                        new Button("Led Off")
                            .OnClick(() => device.LedOff()),
                        new Line(),
                        "Set status:",
                        new Select(status, Object.keys(LedStatus).map(key => new SelectOption(key, key.toLowerCase()))).MarginLeft(8),
                        new Button("Set").Class("uk-button uk-button-danger").MarginLeft(8)
                            .OnClick(() => device.SetLedStatus(status.value)),
                        new Line(),
                        "🟢", new Select(greenLed, { "On": 1, "Off": 0 }).MarginLeft(8).MarginRight(12),
                        "🔴", new Select(redLed, { "On": 1, "Off": 0 }).MarginLeft(8),
                        new Button("Set").Class("uk-button uk-button-danger").MarginLeft(8)
                            .OnClick(() => device.SetLeds(+greenLed.value, +redLed.value))
                    ))
        );

        this.RefreshIoState();
    }

    private async RefreshIoState(): Promise<void>
    {
        const ioState = await this.device.RequestIoState();
        // console.log("IO state", ioState);
        this.ioStateViewer.Refresh(ioState);
    }
}
